import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getApiBase } from '../utils/apiBase';
import { hasGuideExportPreviewed } from '../utils/guideProgress';

const API = getApiBase();

export async function fetchTreeCoreProgress(treeId) {
  const progress = {
    peopleCount: 0,
    storyCount: 0,
    voiceCount: 0,
    documentCount: 0,
    photoStoryCount: 0,
    exportPreviewed: hasGuideExportPreviewed(),
    firstPersonId: null,
  };

  if (!treeId) return progress;

  try {
    const { data: people } = await axios.get(`${API}/individuals/${treeId}`);
    const rows = Array.isArray(people) ? people : [];
    progress.peopleCount = rows.length;
    progress.firstPersonId = rows[0]?.id || null;

    for (const person of rows.slice(0, 20)) {
      try {
        const [storiesRes, docsRes] = await Promise.all([
          axios.get(`${API}/individuals/${person.id}/stories`),
          axios.get(`${API}/documents/${person.id}`),
        ]);
        const stories = storiesRes.data || [];
        const docs = docsRes.data || [];
        progress.storyCount += stories.length;
        progress.documentCount += docs.length;
        progress.voiceCount += stories.filter((s) => s.audio_url || s.voice_memory_url).length;
        progress.photoStoryCount += stories.filter(
          (s) => s.cover_image_url || s.featured_image_url || (s.images && s.images.length > 0),
        ).length;
      } catch {
        /* per-person */
      }
    }
  } catch {
    /* tree load */
  }

  return progress;
}

export function useTreeCoreProgress(treeId) {
  const { token, authReady } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!treeId || !token) {
      setProgress(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const p = await fetchTreeCoreProgress(treeId);
      setProgress(p);
      return p;
    } finally {
      setLoading(false);
    }
  }, [treeId, token]);

  useEffect(() => {
    if (!authReady) return undefined;
    let cancelled = false;
    (async () => {
      const p = await fetchTreeCoreProgress(treeId);
      if (!cancelled) {
        setProgress(p);
        setLoading(false);
      }
    })();
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [treeId, authReady, refresh]);

  return { progress, loading, refresh };
}

export function deriveCoreJourneyStepStatus(progress) {
  const p = progress || {};
  const hasPhoto = (p.documentCount || 0) > 0 || (p.photoStoryCount || 0) > 0;
  const hasMemory = (p.storyCount || 0) > 0;
  const canPreview = hasMemory || hasPhoto || (p.peopleCount || 0) > 0;

  return {
    first_person: (p.peopleCount || 0) >= 1,
    second_person: (p.peopleCount || 0) >= 2,
    first_memory: hasMemory,
    first_photo: hasPhoto,
    preview_book: p.exportPreviewed || (hasMemory && (p.peopleCount || 0) >= 1),
    canPreview,
    readiness: {
      people: (p.peopleCount || 0) > 0,
      memories: hasMemory,
      photos: hasPhoto,
      preview: canPreview,
    },
  };
}
